import { useCallback, useEffect } from 'react';

import { useTheme } from '@mui/material';
import { HouseIcon, MapPlusIcon } from 'lucide-react';
import {
  Navigate,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { Cma_Report_Source_Enum_Enum } from '@graphql/__generated__/graphql';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import {
  type DrawerTab,
  generateTab,
} from '@modules/_shared/components/drawer/DrawerTopBar';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import CreateCMAFromLead from '@modules/create-cma-report/CreateCMAFromLead';
import CreateCMAFromScratch from '@modules/create-cma-report/CreateCMAFromScratch';
import { useGenerateCMAReport } from '@modules/create-cma-report/useGenerateCMAReport';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { useLocale } from '@shared/hooks/locale';

type CreateCMAReportProps = {
  onClose: () => void;
  onCreated: (newCMAReportId: string) => void;
};

const CreateCMAReport = ({ onClose, onCreated }: CreateCMAReportProps) => {
  const { t } = useLocale();
  const { breakpoints } = useTheme();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { me } = useAppData();
  const { track } = useAnalytics();

  const leadId = searchParams.get('id');

  const [generateError, , generateCMAReport, resetGenerateError] =
    useGenerateCMAReport();

  const tabsDef: [string, string, React.ReactElement?][] = [
    [t('From scratch'), 'scratch', <MapPlusIcon />],
    [t('From lead'), 'lead', <HouseIcon />],
  ];

  const tabs: DrawerTab[] = tabsDef.map(([label, value, icon]) =>
    generateTab([searchParams.toString(), label, value, icon]),
  );

  const handleCreateCMA = useCallback(
    async (leadId: string) => {
      const startTime = Date.now();

      const newCMAReportId = await generateCMAReport({
        leadId,
        source: Cma_Report_Source_Enum_Enum.CmaFromLead,
      });

      if (!newCMAReportId) {
        return;
      }

      const baseProperties = getBaseCmaEventProperties(me, newCMAReportId);

      if (baseProperties) {
        track(ANALYTICS_EVENTS.CMA_CREATED, {
          ...baseProperties,
          source: Cma_Report_Source_Enum_Enum.CmaFromLead,
          lead_id: leadId,
          time_to_create_ms: Date.now() - startTime,
        });
      }

      onCreated(newCMAReportId);
      return newCMAReportId;
    },
    [generateCMAReport, onCreated, me, track],
  );

  useEffect(() => {
    if (!leadId) {
      return;
    }

    const createCMAFromLead = async () => {
      const startTime = Date.now();

      const newCMAReportId = await generateCMAReport({
        leadId,
        source: Cma_Report_Source_Enum_Enum.DirectLinkLeadId,
      });

      if (newCMAReportId) {
        const baseProperties = getBaseCmaEventProperties(me, newCMAReportId);

        if (baseProperties) {
          track(ANALYTICS_EVENTS.CMA_CREATED, {
            ...baseProperties,
            source: Cma_Report_Source_Enum_Enum.DirectLinkLeadId,
            lead_id: leadId,
            time_to_create_ms: Date.now() - startTime,
          });
        }

        onCreated(newCMAReportId);
      }
    };

    void createCMAFromLead();
  }, [leadId, generateCMAReport, onCreated, me, track]);

  // If the leadId is in the URL, show fullscreen progress indicator
  if (leadId) {
    return (
      <>
        <MutationErrorSnackbar
          error={generateError}
          onClose={resetGenerateError}
        />
      </>
    );
  }

  return (
    <>
      <Drawer
        onClose={onClose}
        title={t('Create CMA Report')}
        tabs={tabs}
        currentTab={params?.['*'] ?? 'scratch'}
        maxWidth={breakpoints.values.md}
      >
        <Routes>
          <Route
            index
            element={
              <Navigate
                to={{ pathname: 'scratch', search: searchParams.toString() }}
              />
            }
          />
          <Route
            path="scratch"
            element={
              <CreateCMAFromScratch
                onClose={onClose}
                onCreated={onCreated}
                handleCreateCMA={handleCreateCMA}
              />
            }
          />
          <Route
            path="lead"
            element={
              <CreateCMAFromLead
                onClose={onClose}
                handleCreateCMA={handleCreateCMA}
              />
            }
          />
        </Routes>
      </Drawer>

      <MutationErrorSnackbar
        error={generateError}
        onClose={resetGenerateError}
      />
    </>
  );
};

export default CreateCMAReport;
